import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function CompletedTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();


  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none'
    },
    floatingOrb: {
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(40px)',
      opacity: 0.3,
      animation: 'float 8s ease-in-out infinite'
    },
    orb1: { width: '300px', height: '300px', background: colors.primary, top: '10%', left: '5%', animationDelay: '0s' },
    orb2: { width: '200px', height: '200px', background: colors.secondary, top: '60%', right: '10%', animationDelay: '2s' },
    orb3: { width: '250px', height: '250px', background: colors.accent, bottom: '20%', left: '15%', animationDelay: '4s' },
    orb4: { width: '180px', height: '180px', background: colors.success, top: '30%', right: '20%', animationDelay: '6s' },
    gridOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: { padding: '40px 40px 30px', position: 'relative', zIndex: 2 },
    headerContent: { maxWidth: '1200px', margin: '0 auto' },
    greeting: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '15px' },
    pageTitle: {
      fontSize: '3rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    welcomeBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '8px 16px',
      borderRadius: '20px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    badgeText: { color: 'white', fontWeight: '600', fontSize: '0.9rem' },
    badgeRole: {
      background: colors.success,
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.7rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    pageSubtitle: { color: colors.textLight, fontSize: '1.2rem', margin: 0, maxWidth: '600px' },
    messageAlert: {
      color: 'white',
      padding: '20px',
      borderRadius: '15px',
      margin: '0 40px 30px',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1200px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    alertIcon: { fontSize: '1.5rem' },
    alertMessage: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', color: 'white' },
    alertClose: {
      background: 'none',
      border: 'none',
      color: colors.danger,
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '8px',
      transition: 'background 0.3s ease'
    },
    content: { padding: '0 40px 40px', maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '25px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.success}, ${colors.accent})`,
      color: 'white',
      padding: '25px 30px'
    },
    cardTitle: { fontSize: '1.8rem', fontWeight: '700', margin: 0 },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '100px 40px',
      color: 'white',
      textAlign: 'center'
    },
    loadingText: {
      fontSize: '2rem',
      margin: '0 0 20px 0',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    loadingSubtext: { color: colors.textLight, margin: 0, fontSize: '1.2rem' },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    tableHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      borderBottom: `2px solid ${colors.borderDark}`,
      padding: '15px 12px',
      fontWeight: '600'
    },
    tableRow: { borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: 'white' },
    tableCell: { padding: '12px', verticalAlign: 'middle' },
    actionBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '10px 20px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      textDecoration: 'none'
    },
    primaryBtn: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }
  };

  useEffect(() => {
    fetchCompletedTasks();
  }, []);

  const fetchCompletedTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      const editorId = sessionStorage.getItem('userId');
      if (!editorId) throw new Error('Editor ID not found. Please login again.');
      const response = await fetch(`${API_BASE}/get_editor_tasks.php?editor_id=${editorId}`);
      const data = await response.json();
      if (data.success && data.data && Array.isArray(data.data.tasks)) {
        const completedTasks = data.data.tasks.filter(task => 
          ['completed', 'approved', 'Completed', 'Approved'].includes(task.status) || 
          ['Completed', 'Approved'].includes(task.display_status)
        );
        const tasksWithEditedStatus = completedTasks.map(task => ({
          ...task,
          edited_status: task.edited_video_link ? 'completed' : 'pending'
        }));
        setTasks(tasksWithEditedStatus);
      } else {
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      setMessage('❌ Error loading tasks: ' + error.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRawLink = (link) => {
    if (isLinkAvailable(link)) {
      let fLink = link.startsWith('http') ? link : 'https://' + link;
      window.open(fLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenReferenceLink = (link) => {
    if (isLinkAvailable(link)) {
      let fLink = link.startsWith('http') ? link : 'https://' + link;
      window.open(fLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenEditedLink = (task) => {
    if (task.edited_video_link) {
      window.open(task.edited_video_link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleVideoNameClick = (task) => {
    navigate('/task-details', { state: { task: { ...task, videoName: task.video_name, client: task.client_name } } });
  };

  const handleClientClick = (clientId, clientName) => {
    navigate('/client-tasks', { state: { clientId, clientName } });
  };

  const isLinkAvailable = (link) => link && link !== '-' && link !== 'NULL' && link !== 'null' && link.trim() !== '';

  const getStatusBadge = (status) => {
    const config = {
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-circle' },
    }[status.toLowerCase()] || { class: 'bg-success text-white', text: status, icon: 'fa-check' };
    return <span className={`badge ${config.class} fs-6`}><i className={`fas ${config.icon} me-1`}></i>{config.text}</span>;
  };

  const getEditedStatusBadge = (editedStatus) => {
    const config = editedStatus === 'completed' 
      ? { class: 'bg-success text-white', text: 'Edited', icon: 'fa-check-circle' }
      : { class: 'bg-secondary text-white', text: 'Not Edited', icon: 'fa-pen' };
    return <span className={`badge ${config.class} fs-6`}><i className={`fas ${config.icon} me-1`}></i>{config.text}</span>;
  };

  const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loadingState}>
            <div className="loading-spinner" style={styles.spinner}></div>
            <h3 style={styles.loadingText}>Loading Completed Tasks</h3>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div className="greeting" style={styles.greeting}>
              <h1 style={styles.pageTitle}><i className="fas fa-check-circle me-2"></i>Completed Tasks</h1>
            </div>
            <p style={styles.pageSubtitle}>Tasks you have completed and submitted</p>
          </div>
        </div>

        <div style={styles.content}>
          <div className="tasks-card" style={styles.formCard}>
            <div style={styles.cardHeader}><h3 style={styles.cardTitle}><i className="fas fa-list me-2"></i>Completed Task List</h3></div>
            <div style={{overflowX: 'auto'}}>
              <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>ID</th>
                    <th style={styles.tableHeader}>Video Name</th>
                    <th style={styles.tableHeader}>Client</th>
                    <th style={styles.tableHeader}>Operator</th>
                    <th style={styles.tableHeader}>Video Count</th>
                    <th style={styles.tableHeader}>Raw Link</th>
                    <th style={styles.tableHeader}>Reference Link</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Edited Status</th>
                    <th style={styles.tableHeader}>Shoot Date</th>
                    <th style={styles.tableHeader}>Edited Link</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => (
                    <tr key={task.id} style={styles.tableRow}>
                      {/* ID Column - White Color, No Icon */}
                      <td style={{...styles.tableCell, fontWeight: 'bold', color: '#FFFFFF'}}>
                        #{task.id}
                      </td>
                      
                      {/* Video Name Column - White Color, No Icon */}
                      <td style={styles.tableCell}>
                        <button 
                          className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                          onClick={() => handleVideoNameClick(task)}
                          style={{ color: '#FFFFFF', fontWeight: '600', textAlign: 'left' }}
                        >
                          {task.video_name || 'Unnamed Video'}
                        </button>
                      </td>

                      {/* Client Column - White Color, No Icon */}
                      <td style={styles.tableCell}>
                        <button 
                          className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                          onClick={() => handleClientClick(task.client_id, task.client_name)}
                          style={{ color: '#FFFFFF', fontWeight: '600', textAlign: 'left' }}
                        >
                          {task.client_name || 'N/A'}
                        </button>
                      </td>

                      <td style={styles.tableCell}>
                        <span style={{color: 'white', fontWeight: '600'}}>
                          {task.operator_name || 'N/A'}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <span className="badge" style={{background: colors.accent, color: 'white'}}>{task.video_count || 1}</span>
                      </td>
                      <td style={styles.tableCell}>
                        <button 
                          style={{
                            background: isLinkAvailable(task.raw_link) ? `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` : 'rgba(255, 255, 255, 0.1)',
                            color: 'white', border: `1px solid ${colors.borderDark}`, padding: '6px 12px', borderRadius: '8px',
                            cursor: isLinkAvailable(task.raw_link) ? 'pointer' : 'not-allowed', opacity: isLinkAvailable(task.raw_link) ? 1 : 0.6
                          }}
                          onClick={() => handleOpenRawLink(task.raw_link)}
                        >View</button>
                      </td>
                      <td style={styles.tableCell}>
                        <button 
                          style={{
                            background: isLinkAvailable(task.reference_video_link) ? `linear-gradient(135deg, ${colors.accent}, ${colors.primary})` : 'rgba(255, 255, 255, 0.1)',
                            color: 'white', border: `1px solid ${colors.borderDark}`, padding: '6px 12px', borderRadius: '8px',
                            cursor: isLinkAvailable(task.reference_video_link) ? 'pointer' : 'not-allowed', opacity: isLinkAvailable(task.reference_video_link) ? 1 : 0.6
                          }}
                          onClick={() => handleOpenReferenceLink(task.reference_video_link)}
                        >View</button>
                      </td>
                      <td style={styles.tableCell}>{getStatusBadge(task.display_status || task.status)}</td>
                      <td style={styles.tableCell}>{getEditedStatusBadge(task.edited_status)}</td>
                      <td style={styles.tableCell}><small style={{ color: colors.textLight }}>{formatDate(task.shoot_date)}</small></td>
                      <td style={styles.tableCell}>
                        <button
                          style={{
                            background: isLinkAvailable(task.edited_video_link) ? `linear-gradient(135deg, ${colors.success}, ${colors.accent})` : 'rgba(255, 255, 255, 0.1)',
                            color: 'white', border: `1px solid ${colors.borderDark}`, padding: '6px 12px', borderRadius: '8px',
                            cursor: isLinkAvailable(task.edited_video_link) ? 'pointer' : 'not-allowed', opacity: isLinkAvailable(task.edited_video_link) ? 1 : 0.6
                          }}
                          onClick={() => handleOpenEditedLink(task)}
                        >View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes float { 0%, 100% { transform: translate(0px, 0px) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } }
        .table-hover tbody tr:hover { background: rgba(255, 255, 255, 0.05) !important; }
      `}</style>
    </Layout>
  );
}

export default CompletedTasks;